import { ReparoApi } from './index';

// Frontend-facing type — kept stable so the container doesn't need to know
// the underlying table is `purchase_orders`. The adapter functions below
// translate between the backend's wire shape (purchase-order DTO) and this
// frontend shape.

export type ProcurementOrderStatus =
    | 'DRAFT'
    | 'ISSUED'
    | 'IN_PRODUCTION'
    | 'READY_TO_SHIP'
    | 'SHIPPED'
    | 'AT_DESTINATION'
    | 'PARTIALLY_RECEIVED'
    | 'RECEIVED'
    | 'CANCELLED';

export type AdditionalCharge = {
    label: string;
    amount: number;
};

export type ProcurementOrderItem = {
    id: string;
    procurementOrderId: string;
    merchantId: string;
    description: string;
    materialId: string | null;
    productId: string | null;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    /** The line's GST slab. Null is "nobody stated one", never 0%. */
    gstRate: number | null;
    sortOrder?: number;
    receivedQuantity?: number;
};

export type ProcurementOrder = {
    id: string;
    merchantId: string;
    poNumber: string;
    poDate: string;
    expiryDate: string | null;
    vendorId: string | null;
    vendorName: string | null;
    vendorEmail: string | null;
    vendorAddress: string | null;
    vendorGstin: string | null;
    status: ProcurementOrderStatus;
    billToName: string | null;
    billToAddress: string | null;
    billToGstin: string | null;
    shipToName: string | null;
    shipToAddress: string | null;
    shipToGstin: string | null;
    /** Place of supply, as a GST state code — '06'. Decides IGST vs CGST+SGST. */
    shipToStateCode: string | null;
    shipToEmail: string | null;
    contactName: string | null;
    contactEmail: string | null;
    additionalCharges: AdditionalCharge[];
    currency: string;
    subtotal: number;
    taxRate: number;
    taxAmount: number;
    total: number;
    notes: string | null;
    termsAndConditions: string | null;
    sentAt: string | null;
    inProductionAt: string | null;
    cancelledAt: string | null;
    manualReceivedAt: string | null;
    /** WMS back-links — the PO this shipment became, and/or the GRN raised for it. */
    wmsPoCode: string | null;
    wmsGrnCode: string | null;
    wmsGrnStatus: 'CREATED' | 'COMPLETED' | null;
    wmsPushedAt: string | null;
    warehouseId: string | null;
    readinessDate: string | null;
    departureDate: string | null;
    arrivalDate: string | null;
    deliveryDate: string | null;
    /** What happened, as distinct from the plan dates above. Status derives from these. */
    shippedAt: string | null;
    arrivedAt: string | null;
    deliveredAt: string | null;
    /** The freight booking on this leg. */
    forwarderVendorId: string | null;
    forwarderName: string | null;
    forwarderTermsOptionId: string | null;
    freightAmount: number | null;
    freightCurrency: string | null;
    trackingRef: string | null;
    expectedReadinessDate?: string | null;
    expectedDepartureDate?: string | null;
    expectedArrivalDate?: string | null;
    expectedDeliveryDate?: string | null;
    itemCount?: number;
    totalUnits?: number;
    receivedUnits?: number;
    /** Σ quantity × product CBM over product lines that carry one. Shipments only. */
    totalCbm?: number | null;
    /** The CBM cap this shipment was created against. Shipments only. */
    maxCbmPerShipment?: number | null;
    items?: ProcurementOrderItem[];
};

export type CreateProcurementOrderItemInput = {
    description: string;
    materialId?: string | null;
    productId?: string | null;
    quantity: number;
    unitPrice: number;
    gstRate?: number | null;
};

export type CreateProcurementOrderInput = {
    status?: ProcurementOrderStatus;
    poNumber?: string | null;
    poDate?: string | null;
    expiryDate?: string | null;
    vendorId?: string | null;
    vendorName?: string | null;
    vendorEmail?: string | null;
    vendorAddress?: string | null;
    vendorGstin?: string | null;
    billToName?: string | null;
    billToAddress?: string | null;
    billToGstin?: string | null;
    shipToName?: string | null;
    shipToAddress?: string | null;
    shipToGstin?: string | null;
    /** Place of supply, as a GST state code — '06'. Decides IGST vs CGST+SGST. */
    shipToStateCode?: string | null;
    shipToEmail?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    currency?: string;
    taxRate?: number;
    notes?: string | null;
    termsAndConditions?: string | null;
    items: CreateProcurementOrderItemInput[];
    additionalCharges?: AdditionalCharge[];
    /**
     * If set, the PO is pegged to this Work Order — backend writes
     * work_order_allocations rows after the PO + items are saved.
     */
    workOrderId?: string;
};

// ─── Adapters between backend (purchase_orders) and frontend shape ──

type RawItem = {
    purchaseOrderId?: string;
    merchantId?: string;
    itemId?: string;
    lineItemId?: string;
    itemType?: string;
    description?: string | null;
    quantity?: number | string;
    unitPrice?: number | string | null;
    lineTotal?: number | string | null;
    gstRate?: number | string | null;
    sortOrder?: number;
    receivedQuantity?: number | string;
};

export type RawOrder = {
    purchaseOrderId?: string;
    merchantId?: string;
    date?: string | null;
    expiryDate?: string | null;
    vendorId?: string | null;
    vendorName?: string | null;
    vendorEmail?: string | null;
    vendorAddress?: string | null;
    vendorGstin?: string | null;
    status?: string;
    billToName?: string | null;
    billToAddress?: string | null;
    billToGstin?: string | null;
    shipToName?: string | null;
    shipToAddress?: string | null;
    shipToGstin?: string | null;
    /** Place of supply, as a GST state code — '06'. Decides IGST vs CGST+SGST. */
    shipToStateCode?: string | null;
    shipToEmail?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    additionalCharges?: AdditionalCharge[];
    currency?: string;
    subtotal?: number | string;
    taxRate?: number | string;
    taxAmount?: number | string;
    total?: number | string;
    notes?: string | null;
    termsAndConditions?: string | null;
    sentAt?: string | null;
    inProductionAt?: string | null;
    cancelledAt?: string | null;
    manualReceivedAt?: string | null;
    wmsPoCode?: string | null;
    wmsGrnCode?: string | null;
    wmsGrnStatus?: string | null;
    wmsPushedAt?: string | null;
    warehouseId?: string | null;
    readinessDate?: string | null;
    departureDate?: string | null;
    arrivalDate?: string | null;
    deliveryDate?: string | null;
    shippedAt?: string | null;
    arrivedAt?: string | null;
    deliveredAt?: string | null;
    forwarderVendorId?: string | null;
    forwarderName?: string | null;
    forwarderTermsOptionId?: string | null;
    freightAmount?: number | string | null;
    freightCurrency?: string | null;
    trackingRef?: string | null;
    expectedReadinessDate?: string | null;
    expectedDepartureDate?: string | null;
    expectedArrivalDate?: string | null;
    expectedDeliveryDate?: string | null;
    itemCount?: number;
    totalUnits?: number;
    receivedUnits?: number;
    totalCbm?: number | string | null;
    maxCbmPerShipment?: number | string | null;
    items?: RawItem[];
};

function dateToString(value: string | null | undefined): string {
    if (!value) return '';
    // Backend returns YYYY-MM-DD for date columns, full ISO for timestamptz.
    // Either way, slice to YYYY-MM-DD for the frontend's "string date" use.
    return String(value).slice(0, 10);
}

function adaptItem(raw: RawItem): ProcurementOrderItem {
    const itemType = raw.itemType ?? 'product';
    return {
        id: `${raw.purchaseOrderId ?? ''}|${raw.itemId ?? ''}|${raw.lineItemId ?? ''}`,
        procurementOrderId: raw.purchaseOrderId ?? '',
        merchantId: raw.merchantId ?? '',
        description: raw.description ?? '',
        materialId: itemType === 'material' ? raw.itemId ?? null : null,
        productId: itemType === 'product' ? raw.itemId ?? null : null,
        quantity: Number(raw.quantity ?? 0),
        unitPrice: Number(raw.unitPrice ?? 0),
        lineTotal: Number(raw.lineTotal ?? 0),
        gstRate: raw.gstRate == null ? null : Number(raw.gstRate),
        sortOrder: raw.sortOrder,
        receivedQuantity: Number(raw.receivedQuantity ?? 0),
    };
}

export function adaptOrder(raw: RawOrder): ProcurementOrder {
    const poId = raw.purchaseOrderId ?? '';
    return {
        id: poId,
        merchantId: raw.merchantId ?? '',
        poNumber: poId,
        poDate: dateToString(raw.date),
        expiryDate: raw.expiryDate ? dateToString(raw.expiryDate) : null,
        vendorId: raw.vendorId ?? null,
        vendorName: raw.vendorName ?? null,
        vendorEmail: raw.vendorEmail ?? null,
        vendorAddress: raw.vendorAddress ?? null,
        vendorGstin: raw.vendorGstin ?? null,
        status: (raw.status as ProcurementOrderStatus) ?? 'DRAFT',
        billToName: raw.billToName ?? null,
        billToAddress: raw.billToAddress ?? null,
        billToGstin: raw.billToGstin ?? null,
        shipToName: raw.shipToName ?? null,
        shipToAddress: raw.shipToAddress ?? null,
        shipToGstin: raw.shipToGstin ?? null,
        shipToStateCode: raw.shipToStateCode ?? null,
        shipToEmail: raw.shipToEmail ?? null,
        contactName: raw.contactName ?? null,
        contactEmail: raw.contactEmail ?? null,
        additionalCharges: raw.additionalCharges ?? [],
        currency: raw.currency ?? 'INR',
        subtotal: Number(raw.subtotal ?? 0),
        taxRate: Number(raw.taxRate ?? 0),
        taxAmount: Number(raw.taxAmount ?? 0),
        total: Number(raw.total ?? 0),
        notes: raw.notes ?? null,
        termsAndConditions: raw.termsAndConditions ?? null,
        sentAt: raw.sentAt ?? null,
        inProductionAt: raw.inProductionAt ?? null,
        cancelledAt: raw.cancelledAt ?? null,
        manualReceivedAt: raw.manualReceivedAt ?? null,
        wmsPoCode: raw.wmsPoCode ?? null,
        wmsGrnCode: raw.wmsGrnCode ?? null,
        wmsGrnStatus: (raw.wmsGrnStatus as 'CREATED' | 'COMPLETED' | null | undefined) ?? null,
        wmsPushedAt: raw.wmsPushedAt ?? null,
        warehouseId: raw.warehouseId ?? null,
        deliveryDate: raw.deliveryDate ? dateToString(raw.deliveryDate) : null,
        readinessDate: raw.readinessDate ? dateToString(raw.readinessDate) : null,
        departureDate: raw.departureDate ? dateToString(raw.departureDate) : null,
        arrivalDate: raw.arrivalDate ? dateToString(raw.arrivalDate) : null,
        shippedAt: raw.shippedAt ?? null,
        arrivedAt: raw.arrivedAt ?? null,
        deliveredAt: raw.deliveredAt ?? null,
        forwarderVendorId: raw.forwarderVendorId ?? null,
        forwarderName: raw.forwarderName ?? null,
        forwarderTermsOptionId: raw.forwarderTermsOptionId ?? null,
        freightAmount: raw.freightAmount == null ? null : Number(raw.freightAmount),
        freightCurrency: raw.freightCurrency ?? null,
        trackingRef: raw.trackingRef ?? null,
        expectedReadinessDate: raw.expectedReadinessDate ? dateToString(raw.expectedReadinessDate) : null,
        expectedDepartureDate: raw.expectedDepartureDate ? dateToString(raw.expectedDepartureDate) : null,
        expectedArrivalDate: raw.expectedArrivalDate ? dateToString(raw.expectedArrivalDate) : null,
        expectedDeliveryDate: raw.expectedDeliveryDate ? dateToString(raw.expectedDeliveryDate) : null,
        itemCount: raw.itemCount,
        totalUnits: raw.totalUnits,
        receivedUnits: raw.receivedUnits,
        totalCbm: raw.totalCbm == null ? null : Number(raw.totalCbm),
        maxCbmPerShipment: raw.maxCbmPerShipment == null ? null : Number(raw.maxCbmPerShipment),
        items: raw.items?.map(adaptItem),
    };
}

function adaptCreatePayload(
    input: CreateProcurementOrderInput,
): Record<string, unknown> {
    // poNumber (frontend) → purchaseOrderId (backend PK / PO id).
    // Everything else passes through.
    const { poNumber, ...rest } = input;
    return {
        ...rest,
        purchaseOrderId: poNumber ?? undefined,
    };
}

// ─── API methods ────────────────────────────────────────────────

export const listProcurementOrders = async (
    status?: ProcurementOrderStatus,
): Promise<ProcurementOrder[]> => {
    const raw = await ReparoApi<RawOrder[]>({
        method: 'GET',
        url: status
            ? `/procurement-orders?status=${status}`
            : '/procurement-orders',
    });
    return (raw ?? []).map(adaptOrder);
};

export const getProcurementOrder = async (
    id: string,
): Promise<ProcurementOrder> => {
    const raw = await ReparoApi<RawOrder>({
        method: 'GET',
        url: `/procurement-orders/${id}`,
    });
    return adaptOrder(raw);
};

export const createProcurementOrder = async (
    payload: CreateProcurementOrderInput,
): Promise<ProcurementOrder> => {
    const raw = await ReparoApi<RawOrder>({
        method: 'POST',
        url: '/procurement-orders',
        body: JSON.stringify(adaptCreatePayload(payload)),
    });
    return adaptOrder(raw);
};

export const cancelProcurementOrder = async (
    id: string,
): Promise<ProcurementOrder> => {
    const raw = await ReparoApi<RawOrder>({
        method: 'POST',
        url: `/procurement-orders/${id}/cancel`,
        body: JSON.stringify({}),
    });
    return adaptOrder(raw);
};

export const sendProcurementOrder = async (
    id: string,
    options?: { cc?: string[]; to?: string[]; updateOrderAddress?: boolean },
): Promise<ProcurementOrder> => {
    const cc = (options?.cc ?? [])
        .map((e) => e.trim())
        .filter((e) => e.length > 0);
    const to = (options?.to ?? []).map((e) => e.trim()).filter(Boolean);
    /*
     * `to` is who this send goes to; the order's own recorded contact is untouched unless
     * `updateOrderAddress` says otherwise. Several addresses are fine — the document names the
     * counterparty, `sent_to` records delivery.
     */
    const raw = await ReparoApi<RawOrder>({
        method: 'POST',
        url: `/procurement-orders/${id}/send`,
        body: JSON.stringify({
            ...(cc.length > 0 ? { cc } : {}),
            ...(to.length > 0 ? { to } : {}),
            // Only when the sender ticked it. Absent leaves the order's recorded contact alone;
            // sending is a communication act, not an amendment to the order.
            ...(options?.updateOrderAddress ? { updateOrderAddress: true } : {}),
        }),
    });
    return adaptOrder(raw);
};

export type UpdateProcurementOrderInput = Partial<
    Omit<CreateProcurementOrderInput, 'poNumber' | 'status'>
>;

// Edits the body of a DRAFT PO. Backend rejects with 409 PO_NOT_EDITABLE for
// any other status, and 404 PO_NOT_AUTHORED for synced rows. Status changes
// happen through /send, /cancel, /clone — never through this PATCH.
export const updateProcurementOrder = async (
    id: string,
    payload: UpdateProcurementOrderInput,
): Promise<ProcurementOrder> => {
    const raw = await ReparoApi<RawOrder>({
        method: 'PATCH',
        url: `/procurement-orders/${id}`,
        body: JSON.stringify(payload),
    });
    return adaptOrder(raw);
};

export const cloneProcurementOrder = async (
    id: string,
): Promise<ProcurementOrder> => {
    const raw = await ReparoApi<RawOrder>({
        method: 'POST',
        url: `/procurement-orders/${id}/clone`,
        body: JSON.stringify({}),
    });
    return adaptOrder(raw);
};

export type ProcurementOrderMilestones = {
    readinessDate?: string | null;
    departureDate?: string | null;
    arrivalDate?: string | null;
    deliveryDate?: string | null;
};

export const updateProcurementOrderMilestones = async (
    id: string,
    milestones: ProcurementOrderMilestones,
): Promise<ProcurementOrder> => {
    const raw = await ReparoApi<RawOrder>({
        method: 'POST',
        url: `/procurement-orders/${id}/milestones`,
        body: JSON.stringify(milestones),
    });
    return adaptOrder(raw);
};

export const markProcurementOrderIssued = async (
    id: string,
): Promise<ProcurementOrder> => {
    const raw = await ReparoApi<RawOrder>({
        method: 'POST',
        url: `/procurement-orders/${id}/mark-issued`,
        body: JSON.stringify({}),
    });
    return adaptOrder(raw);
};

export const markProcurementOrderInProduction = async (
    id: string,
): Promise<ProcurementOrder> => {
    const raw = await ReparoApi<RawOrder>({
        method: 'POST',
        url: `/procurement-orders/${id}/mark-in-production`,
        body: JSON.stringify({}),
    });
    return adaptOrder(raw);
};

/**
 * The number a new purchase order would take under a prefix.
 *
 * A PREVIEW, NOT A CLAIM - same contract as the production-order twin. Nothing is reserved;
 * whoever saves first takes the number, and the server settles a tie by moving the loser on.
 */
export const peekNextPoNumber = async (
    prefix?: string,
    padWidth?: number,
): Promise<string> => {
    const params = new URLSearchParams();
    if (prefix) params.set('prefix', prefix);
    if (padWidth) params.set('padWidth', String(padWidth));
    const query = params.toString();

    const resp = await ReparoApi<{ orderNumber: string }>({
        method: 'GET',
        url: `/orders/next-number${query ? `?${query}` : ''}`,
    });
    return resp?.orderNumber ?? '';
};
